import React, { useState, useEffect } from 'react';
import * as _ from 'lodash';
import { Select } from '@fabric-ds/react';
import { useDeviceSize } from 'common/lib/hooks/useDeviceSize';
import { formatPriceLabelWithUnit } from 'common/lib/helpers/formatPrice';
import { displayPROMorBRA } from 'common/lib/helpers/TextUtils';
import { ImageGallery } from '@finn-no/object-page-image-gallery-component';
import { useDataContext } from 'common/lib/helpers/DataContext.js';
import { UNIT_APARTMENT, UNIT_BEDROOMS, UNIT_FLOOR, UNIT_FLOORPLAN, UNIT_PRICE, UNIT_SIZE } from './constants.js';
import * as S from './styles';
import Table from './Table';
import { FloorPlanButton } from './FloorPlanButton';
import { getUnits } from './unitFilter';

const numberOfUnitsWithFloor = units => {
    return units.filter(unit => unit.floor).length;
};

const mapUnitsToTableData = (data, setSelectedFloorPlanUnit, selectedFloorPlanUnit) => {
    return data.map(unit => ({
        apartment: truncateText(unit.apartmentNumber || '-'),
        floor: Number.isFinite(unit.floor) ? unit.floor : '-',
        bedrooms: unit.bedrooms || unit.bedrooms === 0 ? unit.bedrooms : '-',
        primaryRoomArea: displayPROMorBRA(unit.primaryRoomArea, unit.usableArea),
        totalPrice: getUnitPriceText(unit),
        finnkode: unit.finnkode,
        adStatus: unit.adStatus,
        floorPlan: unit.floorPlans && unit.floorPlans.length > 0 && (
            <FloorPlanButton
                setSelectedFloorPlanUnit={setSelectedFloorPlanUnit}
                selectedFloorPlanUnit={selectedFloorPlanUnit}
                unit={unit}
            />
        ),
    }));
};

const truncateText = text => {
    return _.truncate(text, {
        length: 10,
        omission: '…',
    });
};

const getUnitPriceText = unit => {
    const { isSold, totalPrice } = unit;
    if (isSold === true || isSold === 'true') {
        return 'Solgt';
    }
    return totalPrice ? formatPriceLabelWithUnit(totalPrice, 'kr') : '-';
};

const tableOrdering = (orderBy, setOrderBy, isMobile) => {
    return (
        <S.BaseLineRow>
            <p>Sorter etter </p>
            <div className="pl-8">
                <Select aria-label="Sorter enheter" value={orderBy} onChange={event => setOrderBy(event.target.value)}>
                    <option value={UNIT_APARTMENT.accessor}>{UNIT_APARTMENT.Header.toLowerCase()}</option>
                    {!isMobile && <option value={UNIT_FLOOR.accessor}>{UNIT_FLOOR.Header.toLowerCase()}</option>}
                    <option value={UNIT_SIZE.accessor}>{UNIT_SIZE.Header.toLowerCase()}</option>
                    <option value={UNIT_BEDROOMS.accessor}>{UNIT_BEDROOMS.Header.toLowerCase()}</option>
                    <option value={UNIT_PRICE.accessor}>{UNIT_PRICE.Header.toLowerCase()}</option>
                </Select>
            </div>
        </S.BaseLineRow>
    );
};

const showAllUnitsButton = (units, setShowAll, showAll) => {
    return (
        units.length > 1 && (
            <button
                className="button button--secondary button--small"
                type="button"
                onClick={() => setShowAll(!showAll)}
            >
                {showAll ? 'Vis færre enheter' : 'Se flere enheter'}
            </button>
        )
    );
};

const UnitSelector = () => {
    const { pictureUrl } = useDataContext();
    const [showAll, setShowAll] = useState(false);
    const [orderBy, setOrderBy] = useState(UNIT_APARTMENT.accessor);
    const [selectedFloorPlanUnit, setSelectedFloorPlanUnit] = useState(null);
    const [selectedFloorplanImages, setSelectedFloorPlanImages] = useState(null);

    const [hideSold, setHideSold] = useState(true);

    const { isMobile } = useDeviceSize();

    const { units, userFilteredUnits, isUnitsHiddenBySearchFilters } = getUnits(orderBy, showAll, false);
    const activeShowAllUnitsButton = showAll || isUnitsHiddenBySearchFilters;
    const allUnitsSold = userFilteredUnits.filter(unit => unit.isSold).length === userFilteredUnits.length;
    const anySoldUnits = userFilteredUnits.filter(unit => unit.isSold).length > 0 && !allUnitsSold;

    const renderedUnits =
        hideSold && !allUnitsSold ? userFilteredUnits.filter(unit => !unit.isSold) : userFilteredUnits;

    useEffect(() => {
        const floorplanImages =
            selectedFloorPlanUnit &&
            selectedFloorPlanUnit.floorPlans &&
            selectedFloorPlanUnit.floorPlans.map(image => {
                return {
                    description: image.caption,
                    width: image.width,
                    height: image.height,
                    uri: pictureUrl + image.path,
                };
            });

        setSelectedFloorPlanImages(floorplanImages);
    }, [selectedFloorPlanUnit]);

    return _.isEmpty(units) ? null : (
        <div className="py-32">
            <div className="w-full">
                <h3 className="font-bold">{units.length === 1 ? 'Enhet i prosjektet' : 'Enheter i prosjektet'}</h3>

                <S.CenterLineRow className="mb-16">
                    {units.length > 1 && tableOrdering(orderBy, setOrderBy, isMobile)}

                    <div>
                        {anySoldUnits && (
                            <button
                                className={`button button--secondary button--small ${
                                    activeShowAllUnitsButton && 'mr-8'
                                }`}
                                type="button"
                                onClick={() => setHideSold(!hideSold)}
                            >
                                {hideSold ? 'Vis solgte enheter' : 'Skjul solgte enheter'}
                            </button>
                        )}
                        {activeShowAllUnitsButton && showAllUnitsButton(units, setShowAll, showAll)}
                    </div>
                </S.CenterLineRow>
            </div>

            <S.UnitTableWrapper mobile={isMobile}>
                <Table
                    columns={
                        numberOfUnitsWithFloor(renderedUnits) > 0
                            ? [UNIT_APARTMENT, UNIT_FLOOR, UNIT_SIZE, UNIT_BEDROOMS, UNIT_PRICE, UNIT_FLOORPLAN]
                            : [UNIT_APARTMENT, UNIT_SIZE, UNIT_BEDROOMS, UNIT_PRICE, UNIT_FLOORPLAN]
                    }
                    data={mapUnitsToTableData(renderedUnits, setSelectedFloorPlanUnit, selectedFloorPlanUnit)}
                />
                {selectedFloorplanImages && <ImageGallery images={selectedFloorplanImages} />}
            </S.UnitTableWrapper>
        </div>
    );
};

export default UnitSelector;
